
"use client";

import { useState, useMemo, useEffect, useCallback, useRef, Suspense } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { Card } from "@speakai/ui/card";
import { Button } from "@speakai/ui/button";
import { Badge } from "@speakai/ui/badge";
import { Skeleton } from "@speakai/ui/skeleton";
import { useToast } from "@speakai/ui/toast";
import { Minus, Plus, Check, Info } from "lucide-react";
import { format } from "date-fns";
import { motion } from "motion/react";
import { TrustBar, TestimonialsSection } from "@/features/pricing/components/social-proof";
import { FaqSection } from "@/features/pricing/components/faq-section";
import { cn } from "@/lib/utils/cn";
import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { track, trackPageview, trackFunnel, trackFailure } from "@/lib/integrations/sentry-tracking";
import { useSubscription } from "@/features/subscription/queries";
import { useAppStore } from "@/stores";
import { useOffers } from "@/features/subscription/payment-queries";
import {
  useRetrySubscriptionStripe,
  useRetrySubscriptionPaddle,
} from "@/features/subscription/mutations";
import { usePaymentMethods } from "@/features/subscription/payment-queries";
import { AddSource } from "@/features/subscription/components/add-source";
import { RetryPaymentDialog } from "@/features/subscription/components/retry-payment-dialog";
import { useAuth } from "@/hooks/use-auth";
import { bookingCalendarUrl, routeBookingTarget, withBookingUtms } from "@/lib/utils/calendly";
import { useIntercom } from "@/hooks/use-intercom";
import { ManagedPlanNote } from "@/features/pricing/components/managed-plan-note";
import { usePermissions } from "@/hooks/use-permissions";
import { usePricingPlans } from "@/features/subscription/hooks/use-pricing-plans";
import { CreateSubscription } from "@/features/subscription/components/create-subscription";
import { CancelFlow } from "@/features/subscription/components/cancel-flow";
import {
  SubscriptionPlanId,
  SubscriptionStatus,
  PaymentSourceType,
  type UserSubscription,
  type PlanDurations,
  type PlanTypes,
  type Offer,
} from "@/features/subscription/types";
import { SPEAK_PRICING_URL } from "@/config/constants";
import { PRO_OFFER_ID } from "@/config/billing-constants";
import { subscriptionKeys, paymentKeys, dashboardKeys } from "@/queries/keys";
import { trackPricingCtaClick, pushDataLayer } from "@/lib/integrations/ga4";
import {
  DURATION_MONTHLY,
  DURATION_YEARLY,
  HIGH_VALUE_COST_THRESHOLD,
  HIGH_VALUE_YEARLY_THRESHOLD,
  USE_CASES,
  PAYG_RATES,
  PAYG_FEATURES,
  PRO_RATES,
  PRO_FEATURES,
  CUSTOM_RATES,
  CUSTOM_FEATURES,
  type PricingCardRate,
  type PricingFeatureEntry,
} from "./pricing-data";
import { useCredits, creditCapabilityLines, formatCredits } from "@/features/subscription/hooks/use-credits";

export function PlanIncludesCard({
  subscription,
  isPendingCancellation,
  isPendingReview,
  isCurrentPayg,
}: {
  subscription: UserSubscription;
  isPendingCancellation: boolean;
  isPendingReview: boolean;
  isCurrentPayg: boolean;
}) {
  const { t } = useTranslation();
  const f = subscription.features;

  if (isCurrentPayg) {
    return (
      <div className="rounded-xl border border-border bg-muted/30 px-5 py-4">
        <p className="text-sm text-muted-foreground">
          {t("PRICING.PLAN_INCLUDES.PAYG_MESSAGE")}
        </p>
      </div>
    );
  }

  const creditMode = subscription.creditMode === true;
  const stats: { label: string; value: string }[] = [
    ...(creditMode
      ? [
        ...((subscription.creditsGranted ?? 0) > 0
          ? [{ label: t("PRICING.PLAN_INCLUDES.CREDITS", "Credits"), value: t("PRICING.PLAN_INCLUDES.CREDITS_PER_MONTH", { credits: formatCredits(subscription.creditsGranted ?? 0), defaultValue: "{{credits}} / mo" }) }]
          : []),
        { label: t("PRICING.PLAN_INCLUDES.CREDITS_AVAILABLE", "Available to spend"), value: formatCredits(subscription.creditsRemaining ?? 0) },
      ]
      : f.transcript.totalMinutes > 0
        ? [{ label: t("PRICING.PLAN_INCLUDES.TRANSCRIPTION"), value: subscription.durationId === DURATION_YEARLY.id ? t("PRICING.PLAN_INCLUDES.HRS_PER_YEAR", { hours: Math.round(f.transcript.totalMinutes / 60) }) : t("PRICING.PLAN_INCLUDES.HRS_PER_MONTH", { hours: Math.round(f.transcript.totalMinutes / 60) }) }]
        : []),
    ...(f.collaboration.allowUsers > 0
      ? [{ label: t("PRICING.PLAN_INCLUDES.TEAM_SEATS"), value: f.collaboration.allowUsers === 1 ? t("PRICING.PLAN_INCLUDES.SEAT_ONE") : t("PRICING.PLAN_INCLUDES.SEATS_MANY", { count: f.collaboration.allowUsers }) }]
      : []),
    ...(!creditMode && f.prompt.totalCharacters > 0
      ? [{ label: t("PRICING.PLAN_INCLUDES.AI_CHAT"), value: subscription.durationId === DURATION_YEARLY.id ? t("PRICING.PLAN_INCLUDES.CHARS_PER_YEAR", { chars: new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(f.prompt.totalCharacters) }) : t("PRICING.PLAN_INCLUDES.CHARS_PER_MONTH", { chars: new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(f.prompt.totalCharacters) }) }]
      : []),
    ...(f.import.fileSizeInGB > 0
      ? [{ label: t("PRICING.PLAN_INCLUDES.MAX_UPLOAD"), value: t("PRICING.PLAN_INCLUDES.GB_PER_FILE", { size: f.import.fileSizeInGB }) }]
      : []),
    ...(f.delete.mediaInDays !== 0
      ? [{ label: t("PRICING.PLAN_INCLUDES.DATA_RETENTION"), value: f.delete.mediaInDays === -1 ? t("PRICING.PLAN_INCLUDES.PERMANENT_STORAGE") : t("PRICING.PLAN_INCLUDES.DAYS_RETENTION", { days: f.delete.mediaInDays }) }]
      : []),
  ];

  const boolFeatures = [
    f.meetingAssistant.isActive && t("PRICING.PLAN_INCLUDES.FEATURE_MEETING_ASSISTANT"),
    f.developers.apiKeys && t("PRICING.PLAN_INCLUDES.FEATURE_API"),
    f.developers.webhooks && t("PRICING.PLAN_INCLUDES.FEATURE_WEBHOOKS"),
    f.support.liveChat && t("PRICING.PLAN_INCLUDES.FEATURE_LIVE_CHAT"),
    f.insights.custom && t("PRICING.PLAN_INCLUDES.FEATURE_CUSTOM_INSIGHTS"),
  ].filter(Boolean) as string[];

  if (stats.length === 0 && boolFeatures.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-card px-5 py-4 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {t("PRICING.PLAN_INCLUDES.TITLE")}
        </p>
      </div>

      {isPendingCancellation && subscription.billEndDate && (
        <p className="text-xs text-warning">
          {t("PRICING.PLAN_INCLUDES.CANCELLING_NOTE", {
            date: format(new Date(subscription.billEndDate), "MMM dd, yyyy"),
          })}
        </p>
      )}
      {isPendingReview && (
        <p className="text-xs text-muted-foreground">
          {t("PRICING.PLAN_INCLUDES.PAUSED_NOTE")}
        </p>
      )}

      {stats.length > 0 && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          {stats.map(({ label, value }) => (
            <div key={label} className="rounded-lg bg-muted/50 px-3 py-2.5">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="mt-0.5 text-sm font-semibold text-foreground break-words">{value}</p>
            </div>
          ))}
        </div>
      )}

      {boolFeatures.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {boolFeatures.map((feat) => (
            <span
              key={feat}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground"
            >
              <Check className="h-3 w-3 text-primary" />
              {feat}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}


interface PricingCardProps {
  name: string;
  description: string;
  monthlyPrice: number | null;
  annualPrice: number | null;
  isAnnual: boolean;
  featured?: boolean;
  currentPlan?: boolean;
  rates: PricingCardRate[];
  ratesTitle?: string;
  features: PricingFeatureEntry[];
  featuresTitle?: string;
  ctaText: string;
  ctaVariant?: "primary" | "secondary" | "outline";
  ctaDisabled?: boolean;
  onCtaClick: () => void;
  seats?: number;
  badge?: string;
  priceSubtext?: string;
  ctaSubLabel?: string;
  teamConfigurator?: React.ReactNode;
  enterpriseDiscount?: { originalPrice: number; discountPercent: number };
}

function RateRow({ rate }: { rate: PricingCardRate }) {
  const { t } = useTranslation();
  const label = rate.labelKey ? t(rate.labelKey, rate.label) : rate.label;
  const tooltip = rate.tooltip
    ? rate.tooltipKey
      ? t(rate.tooltipKey, rate.tooltip)
      : rate.tooltip
    : undefined;
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="flex items-center gap-1 text-muted-foreground">
        {label}
        {tooltip && (
          <button
            type="button"
            aria-label={`Tooltip: ${tooltip}`}
            className="group relative inline-flex cursor-help focus-visible:outline-none"
          >
            <Info className="h-3.5 w-3.5 text-muted-foreground/60" aria-hidden="true" />
            <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 hidden w-60 -translate-x-1/2 rounded-lg bg-foreground px-3 py-2 text-xs leading-relaxed text-background shadow-lg group-hover:block group-focus:block">
              {tooltip}
              <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-foreground" />
            </span>
          </button>
        )}
      </span>
      <span className="whitespace-nowrap font-semibold tabular-nums">{rate.value}</span>
    </div>
  );
}

function PricingCard({
  name,
  description,
  monthlyPrice,
  annualPrice,
  isAnnual,
  featured = false,
  currentPlan = false,
  rates,
  ratesTitle = "Per-use rates",
  features,
  featuresTitle,
  ctaText,
  ctaVariant = "primary",
  ctaDisabled = false,
  onCtaClick,
  seats = 1,
  badge,
  priceSubtext,
  ctaSubLabel,
  teamConfigurator,
  enterpriseDiscount,
}: PricingCardProps) {
  const { t } = useTranslation();
  const price = isAnnual ? annualPrice : monthlyPrice;
  const displayPrice =
    price !== null ? `$${(price * seats).toLocaleString()}` : null;
  const billingLabel =
    price !== null
      ? seats > 1
        ? `${seats} users at $${price}/user/mo`
        : isAnnual
          ? `per user, billed annually ($${(price * 12).toLocaleString()}/yr)`
          : "per user, billed monthly"
      : null;

  return (
    <Card
      className={cn(
        "relative flex h-full flex-col gap-5 rounded-2xl p-6 transition-shadow hover:shadow-md",
        featured
          ? "border-2 border-foreground shadow-md"
          : "border border-border",
        currentPlan && "border-primary ring-1 ring-primary"
      )}
    >
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge
            variant="default"
            className="bg-foreground text-background px-3 py-1 text-xs font-semibold"
          >
            {badge}
          </Badge>
        </div>
      )}

      <div>
        <h3 className="text-lg font-semibold">{name}</h3>
        <p className="md:min-h-[4.5rem] text-sm text-muted-foreground">{description}</p>
      </div>
      <div>
        {displayPrice !== null ? (
          <>
            <div className="flex items-baseline gap-1">
              {enterpriseDiscount ? (
                <>
                  <span className="text-2xl font-bold text-muted-foreground line-through">
                    ${enterpriseDiscount.originalPrice}
                  </span>
                  <span className="text-4xl font-bold tracking-tight">
                    {displayPrice}
                  </span>
                  <span className="text-base text-muted-foreground">/mo</span>
                  <Badge className="ml-1.5 bg-success/10 text-success text-xs font-semibold">
                    {enterpriseDiscount.discountPercent}% off
                  </Badge>
                </>
              ) : (
                <>
                  <span className="text-4xl font-bold tracking-tight">
                    {displayPrice}
                  </span>
                  <span className="text-base text-muted-foreground">/mo</span>
                </>
              )}
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {priceSubtext ?? billingLabel}
            </p>
          </>
        ) : (
          <>
            <span className="text-4xl font-bold tracking-tight">
              Let&apos;s talk
            </span>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {priceSubtext ?? "Tailored plans for teams of any size"}
            </p>
          </>
        )}
      </div>
      <div className="md:min-h-[60px]">{teamConfigurator}</div>
      <div>
        <Button
          className="w-full whitespace-nowrap"
          variant={
            ctaDisabled
              ? "outline"
              : ctaVariant === "secondary"
                ? "outline"
                : "primary"
          }
          disabled={ctaDisabled}
          onClick={onCtaClick}
          aria-label={`${ctaText} for ${name} plan`}
        >
          {ctaText}
        </Button>
        {/* Reserved even when empty so the rates blocks start at the same
            height on every card. */}
        <p className="mt-2 min-h-4 text-center text-xs text-muted-foreground">
          {ctaSubLabel}
        </p>
      </div>
      {rates.length > 0 && (
        <div className="rounded-lg bg-muted/50 px-4 py-3">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {ratesTitle}
          </p>
          {rates.map((rate) => (
            <RateRow key={rate.label} rate={rate} />
          ))}
        </div>
      )}
      <ul className="flex flex-col gap-1.5">
        {featuresTitle && (
          <li className="text-sm font-semibold">{featuresTitle}</li>
        )}
        {features.map((entry) => {
          const feature = typeof entry === "string" ? { text: entry } : entry;
          const label = feature.key ? t(feature.key, feature.text) : feature.text;
          return (
            // One line, always: lines are written to fit and truncate rather
            // than wrap, so a long translation can never break the rhythm.
            <li key={feature.text} className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 shrink-0 text-success" />
              <span className="min-w-0 truncate text-muted-foreground" title={label}>
                {label}
              </span>
              {feature.isNew && (
                <span className="inline-flex shrink-0 items-center rounded-full border border-foreground/40 px-1.5 py-px text-[10px] font-bold uppercase tracking-wider text-foreground">
                  {t("LAUNCH_BAR.BADGE", "New")}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function BillingToggle({
  isAnnual,
  onChange,
}: {
  isAnnual: boolean;
  onChange: (annual: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center gap-3">
      <button
        type="button"
        className={cn(
          "border-0 p-0 cursor-pointer text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm",
          !isAnnual ? "font-semibold text-foreground" : "text-muted-foreground"
        )}
        onClick={() => onChange(false)}
        aria-label="Select monthly billing"
      >
        {t("PRICING.MONTHLY")}
      </button>
      <button
        type="button"
        role="switch"
        aria-checked={isAnnual}
        aria-label="Toggle annual billing"
        className={cn(
          "relative h-6 w-12 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
          isAnnual ? "bg-primary" : "bg-muted-foreground/30"
        )}
        onClick={() => onChange(!isAnnual)}
      >
        <span
          className={cn(
            "absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-background shadow-sm transition-transform",
            isAnnual && "translate-x-6"
          )}
        />
      </button>
      <button
        type="button"
        className={cn(
          "border-0 p-0 cursor-pointer text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm",
          isAnnual ? "font-semibold text-foreground" : "text-muted-foreground"
        )}
        onClick={() => onChange(true)}
        aria-label="Select annual billing"
      >
        {t("PRICING.YEARLY")}
      </button>
      <Badge className="bg-success/10 text-success text-xs font-semibold">
        {t("PRICING.SAVE_PERCENT")}
      </Badge>
    </div>
  );
}

function TeamConfigurator({
  seats,
  onSeatsChange,
  isAnnual,
  perUserMonthly,
  perUserAnnual,
}: {
  seats: number;
  onSeatsChange: (n: number) => void;
  isAnnual: boolean;
  perUserMonthly: number;
  perUserAnnual: number;
}) {
  const { t } = useTranslation();
  const currentPrice = isAnnual ? perUserAnnual : perUserMonthly;
  const totalMonthly = currentPrice * seats;
  const rawAnnualSavings = (perUserMonthly - perUserAnnual) * seats * 12;
  const annualSavings = Math.max(0, rawAnnualSavings);
  const showContactNudge = seats >= 10;

  const handleIncrement = () => {
    onSeatsChange(Math.min(10, seats + 1));
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3 rounded-lg bg-muted/50 px-4 py-2.5">
        <span className="text-sm font-medium">Team members:</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background text-sm font-semibold transition-colors hover:bg-muted"
            onClick={() => onSeatsChange(Math.max(1, seats - 1))}
            disabled={seats <= 1}
            aria-label="Remove team member"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span className="min-w-[24px] text-center text-sm font-bold tabular-nums">
            {seats}
          </span>
          <button
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background text-sm font-semibold transition-colors hover:bg-muted"
            onClick={handleIncrement}
            disabled={seats >= 10}
            aria-label="Add team member"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
        {seats > 1 && annualSavings > 0 && (
          <span className="ml-auto text-xs text-muted-foreground">
            {isAnnual
              ? `$${(totalMonthly * 12).toLocaleString()}/yr (save $${annualSavings.toLocaleString()})`
              : `Save $${annualSavings.toLocaleString()}/yr with annual`}
          </span>
        )}
      </div>
      {showContactNudge && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-center text-xs text-primary">
          Need more than 10 users?{" "}
          <a
            href="https://speakai.co/enterprise/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline"
          >
            {t("PRICING.ACTION.CONTACT_SALES")}
          </a>{" "}
          for volume pricing.
        </div>
      )}
    </div>
  );
}

function UseCasesSection() {
  const { t } = useTranslation();
  return (
    <section className="space-y-6 text-center">
      <div>
        <h2 className="text-2xl font-bold">{t("PRICING.BUILT_FOR_TEAMS")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Here&apos;s how teams use Speak AI.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {USE_CASES.map((uc) => (
          <Card
            key={uc.name}
            className="border border-border bg-card p-4 text-center transition-shadow hover:shadow-sm"
          >
            <p className="text-sm font-semibold">{uc.name}</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {uc.desc}
            </p>
          </Card>
        ))}
      </div>
    </section>
  );
}

function PricingCardSkeleton() {
  return (
    <Card className="flex flex-col gap-5 rounded-2xl border border-border p-6">
      <Skeleton className="h-5 w-24" />
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-10 w-28" />
      <Skeleton className="h-3 w-36" />
      <div className="space-y-2 rounded-lg bg-muted/50 p-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex justify-between">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-3.5 w-16" />
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-3.5 w-full" />
        ))}
      </div>
      <Skeleton className="mt-auto h-10 w-full rounded-xl" />
    </Card>
  );
}

interface PricingPageContentProps {
  onCheckoutSuccess?: () => void;
}

function PricingPageContent({ onCheckoutSuccess }: PricingPageContentProps = {}) {
  useDocumentTitle("Pricing");
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { canManageBilling } = usePermissions();
  const { sendMessage: intercomSendMessage, show: intercomShow } = useIntercom();
  const toast = useToast();
  const queryClient = useQueryClient();
  const {
    data: subscription,
    isLoading: subscriptionLoading,
    isError: subscriptionError,
  } = useSubscription();
  const { data: offers, isLoading: offersLoading, isError: offersError } = useOffers();

  // Accept ?interval=annual|yearly (powerspot pill uses "annual"; toggle UI
  // uses "yearly") and the legacy ?billing=annual param. All three open the
  // pricing page with the annual tab pre-selected.
  const initialInterval = (() => {
    const interval = searchParams.get("interval");
    if (interval === "annual" || interval === "yearly") return true;
    const billing = searchParams.get("billing");
    if (billing === "annual") return true;
    return false;
  })();
  const [isAnnual, setIsAnnual] = useState(initialInterval);
  const [proSeats, setProSeats] = useState(1);
  const [checkoutPlan, setCheckoutPlan] = useState<PlanTypes | null>(null);
  const [checkoutDuration, setCheckoutDuration] =
    useState<PlanDurations | null>(null);
  const [showCancelFlow, setShowCancelFlow] = useState(false);
  const cancelDeepLinkFired = useRef(false);

  const [showRetryAddSource, setShowRetryAddSource] = useState(false);
  const [showRetryPaymentDialog, setShowRetryPaymentDialog] = useState(false);

  // Reflect the cancel-flow / retry-payment dialog open-state into the store so the exit-intent
  // engine suppresses over it (redundancy gate). Open-state mirror only — no payment logic here.
  const setPaymentDialogOpen = useAppStore((s) => s.setPaymentDialogOpen);
  useEffect(() => {
    setPaymentDialogOpen(showCancelFlow || showRetryPaymentDialog);
    return () => setPaymentDialogOpen(false); // clear on unmount so the flag never sticks open
  }, [showCancelFlow, showRetryPaymentDialog, setPaymentDialogOpen]);

  const retryStripe = useRetrySubscriptionStripe();
  const retryPaddle = useRetrySubscriptionPaddle();

  const isPendingPayment =
    subscription?.status === SubscriptionStatus.PendingPayment;
  const { data: stripeSources } = usePaymentMethods(PaymentSourceType.Stripe);
  const retryProcessing = retryStripe.isPending || retryPaddle.isPending;

  useEffect(() => {
    trackPageview("pricing");
  }, []);

  useEffect(() => {
    const interval = searchParams.get("interval");
    const billing = searchParams.get("billing");
    if (interval === "annual" || interval === "yearly" || billing === "annual") {
      setIsAnnual(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const planId = subscription?.planId;
    const offerId = subscription?.offerId;
    const seats = subscription?.features?.collaboration?.allowUsers;
    if (
      planId === SubscriptionPlanId.Speak_Custom &&
      offerId === PRO_OFFER_ID &&
      typeof seats === "number" &&
      seats > 0
    ) {
      setProSeats(seats);
      setIsAnnual(subscription?.durationId === DURATION_YEARLY.id);
    }
  }, [
    subscription?.planId,
    subscription?.offerId,
    subscription?.durationId,
    subscription?.features?.collaboration?.allowUsers,
  ]);

  const isRevenueCat =
    subscription?.paymentSource === PaymentSourceType.Revenuecat;

  const isSubscribedActive =
    !!subscription &&
    !subscription.isExpired &&
    subscription.planId !== SubscriptionPlanId.Speak_Free_Trial &&
    subscription.planId !== SubscriptionPlanId.Speak_Pay_As_You_Go;
  const isHighValueSubscriber =
    isSubscribedActive &&
    (
      (subscription?.cost?.monthly ?? 0) >= HIGH_VALUE_COST_THRESHOLD ||
      (subscription?.cost?.total ?? 0) >= HIGH_VALUE_YEARLY_THRESHOLD
    );
  const isDirectDeposit =
    isSubscribedActive &&
    subscription?.paymentSource === PaymentSourceType.Direct_Deposit;

  useEffect(() => {
    if (cancelDeepLinkFired.current) return;
    if (subscriptionLoading || !subscription) return;
    const action = searchParams.get("action");
    if (
      action === "cancel" &&
      canManageBilling &&
      !isHighValueSubscriber &&
      !isDirectDeposit &&
      subscription.planId !== SubscriptionPlanId.Speak_Free_Trial &&
      subscription.planId !== SubscriptionPlanId.Speak_Pay_As_You_Go &&
      subscription.paymentSource !== PaymentSourceType.Revenuecat &&
      subscription.status !== SubscriptionStatus.PendingCancellation &&
      subscription.status !== SubscriptionStatus.PendingReview
    ) {
      cancelDeepLinkFired.current = true;
      setShowCancelFlow(true);
    }
  }, [searchParams, subscription, subscriptionLoading, isHighValueSubscriber, isDirectDeposit, canManageBilling]);

  const heroText = useMemo(() => {
    if (isSubscribedActive) return t("PRICING.HERO.TITLE.DEFAULT");
    const goal = user?.onboarding?.goal as string | undefined;
    if (goal?.includes("research")) return t("PRICING.HERO.TITLE.RESEARCH");
    if (goal?.includes("sales")) return t("PRICING.HERO.TITLE.SALES");
    return t("PRICING.HERO.TITLE.DEFAULT");
  }, [user, t, isSubscribedActive]);

  const goalLabel = useMemo(() => {
    if (isSubscribedActive) return null;
    const goal = user?.onboarding?.goal as string | undefined;
    if (!goal) return null;
    if (goal.includes("research")) return t("PRICING.HERO.RECOMMENDED.RESEARCH");
    if (goal.includes("sales")) return t("PRICING.HERO.RECOMMENDED.SALES");
    if (goal.includes("meeting")) return t("PRICING.HERO.RECOMMENDED.MEETINGS");
    if (goal.includes("content")) return t("PRICING.HERO.RECOMMENDED.CONTENT");
    return t("PRICING.HERO.RECOMMENDED.DEFAULT");
  }, [user, t, isSubscribedActive]);

  // Scale carries "Most Popular" (the steer); Pro keeps only the goal-matched
  // recommendation so the two cards never wear the same badge.
  const proBadge = useMemo(() => {
    if (isSubscribedActive) return undefined;
    const goal = user?.onboarding?.goal as string | undefined;
    if (goal?.includes("research")) return t("PRICING.RECOMMENDED_FOR_RESEARCH");
    if (goal?.includes("sales")) return t("PRICING.RECOMMENDED_FOR_SALES");
    if (goal?.includes("meeting")) return t("PRICING.RECOMMENDED_FOR_MEETINGS");
    if (goal?.includes("content")) return t("PRICING.RECOMMENDED_FOR_MEDIA");
    return undefined;
  }, [user, t, isSubscribedActive]);

  const isPendingCancellation =
    subscription?.status === SubscriptionStatus.PendingCancellation;
  const isPendingReview = subscription?.status === SubscriptionStatus.PendingReview;

  const isCurrentPayg =
    subscription?.planId === SubscriptionPlanId.Speak_Pay_As_You_Go;

  const proOffer = useMemo(
    () => offers?.find((offer) => offer.offerId === PRO_OFFER_ID) as Offer | undefined,
    [offers]
  );
  const { creditMode } = useCredits();

  const proRates = useMemo<PricingCardRate[]>(() => {
    if (!creditMode || !proOffer?.credits) return PRO_RATES;
    const storage = PRO_RATES.find((r) => r.label.toLowerCase().includes("storage"));
    return [
      { label: "Monthly credits", value: formatCredits(proOffer.credits) },
      ...(storage ? [storage] : []),
    ];
  }, [creditMode, proOffer]);

  const proFeatures = useMemo<PricingFeatureEntry[]>(() => {
    if (!creditMode || !proOffer?.credits) return PRO_FEATURES;
    // One checkmark per service instead of one four-line "A or B or C or D"
    // sentence: the first line carries the figure, the rest read as "or …".
    const capability = creditCapabilityLines(proOffer.creditValue).map((line, index) => {
      const text = t(line.key, { defaultValue: line.text, ...line.params });
      return {
        text:
          index === 0
            ? text
            : t("PRICING.CREDIT_CAP.OR_LINE", { defaultValue: "or {{line}}", line: text }),
      };
    });
    return [...capability, ...PRO_FEATURES];
  }, [creditMode, proOffer, t]);

  const isCurrentPro =
    subscription?.planId === SubscriptionPlanId.Speak_Custom &&
    !!proOffer &&
    subscription?.offerId === proOffer.offerId;
  const isCurrentCustom =
    subscription?.planId === SubscriptionPlanId.Speak_Custom &&
    (!proOffer || subscription?.offerId !== proOffer.offerId);

  const isTrial =
    subscription?.planId === SubscriptionPlanId.Speak_Free_Trial;
  const isExpiredTrial = isTrial && !!subscription?.isExpired;
  const isEnterprise = subscription?.isEnterprise === true;

  const proMonthly = proOffer ? proOffer.pricing.monthlyPrice : null;
  const proAnnual = proOffer ? proOffer.pricing.annualPrice / 12 : null;
  const currentDuration = isAnnual ? DURATION_YEARLY : DURATION_MONTHLY;

  const subscribedSeats = subscription?.features?.collaboration?.allowUsers ?? 1;
  const hasProChanges =
    isCurrentPro &&
    (proSeats !== subscribedSeats || currentDuration.id !== subscription?.durationId);

  const proEnterpriseDiscount = useMemo(() => {
    if (!isEnterprise || !proOffer?.pricing) return undefined;
    const { originalPrice, discountPercentage } = proOffer.pricing;
    if (!originalPrice || !discountPercentage) return undefined;
    return {
      originalPrice,
      discountPercent: discountPercentage,
    };
  }, [isEnterprise, proOffer]);

  const {
    proMonthly: proMonthlyPlan,
    proYearly: proYearlyPlan,
    payAsYouGo: paygPlan,
  } = usePricingPlans(proSeats);

  const buildProPlanType = useCallback(
    (): PlanTypes | null => (isAnnual ? proYearlyPlan : proMonthlyPlan),
    [isAnnual, proMonthlyPlan, proYearlyPlan]
  );

  const buildPaygPlanType = useCallback((): PlanTypes => paygPlan, [paygPlan]);

  function handleSelectPlan(planType: PlanTypes, duration: PlanDurations) {
    if (!canManageBilling) {
      toast.error(t("PAYMENT.NO_BILLING_PERMISSION", "Contact your account admin to manage billing."));
      return;
    }
    trackFunnel("subscription", "select_plan", {
      plan: planType.displayName,
      billing_cycle: duration.id,
    });
    trackPricingCtaClick(planType.displayName, duration.id);
    setCheckoutPlan(planType);
    setCheckoutDuration(duration);
  }

  async function handleRetryPendingPayment() {
    if (!canManageBilling) {
      toast.error(
        t(
          "PAYMENT.NO_BILLING_PERMISSION",
          "Contact your account admin to manage billing.",
        ),
      );
      return;
    }

    if (!subscription || !isPendingPayment) return;

    if (subscription.paymentSource === PaymentSourceType.Stripe) {
      const hasCard = (stripeSources?.length ?? 0) > 0;
      if (hasCard) {
        setShowRetryPaymentDialog(true);
      } else {
        setShowRetryAddSource(true);
      }
      return;
    }

    if (subscription.paymentSource === PaymentSourceType.Paddle) {
      try {
        await retryPaddle.mutateAsync();
        await Promise.race([
          Promise.allSettled([
            queryClient.refetchQueries({ queryKey: subscriptionKeys.all }),
            queryClient.refetchQueries({ queryKey: paymentKeys.balance() }),
            queryClient.refetchQueries({ queryKey: dashboardKeys.all }),
          ]),
          new Promise<void>((resolve) => setTimeout(resolve, 30_000)),
        ]);
      } catch (err: unknown) {
        const reason = err instanceof Error ? err.message : "paddle_retry_failed";
        const userCancelled = reason === "checkout_closed";
        trackFailure("payment", "fail", {
          error_reason: reason,
          charge_type: "retry",
          payment_method: "paddle",
          failure_type: userCancelled ? "paddle_cancelled" : "paddle_checkout",
        });
        if (!userCancelled) {
          toast.error(t("CREATE_SUBSCRIPTION.FAILED_TITLE"));
        }
      }
    }
  }

  function handleToggleBilling(annual: boolean) {
    setIsAnnual(annual);
    track("payment", "plan_duration", {
      duration: annual ? "yearly" : "monthly",
    });
    pushDataLayer("Pricing", "BillingToggle", annual ? "annual" : "monthly");
  }

  // TODO: CHECK WITH TEAM
  function handleContactSales() {
    track("payment", "contact_sales");
    pushDataLayer("Pricing", "ContactSalesClick", isHighValueSubscriber ? "high_value" : "standard");
    const target = routeBookingTarget(isSubscribedActive, user?.isCompanyDomain);
    trackFunnel("booking", "cta_clicked", { surface: "pricing", target });
    pushDataLayer("Booking", "CTA Clicked", "pricing", undefined, { surface: "pricing", target });
    window.open(
      withBookingUtms(bookingCalendarUrl(target), "pricing", { userId: user?._id }),
      "_blank",
      "noopener,noreferrer",
    );
  }

  const paygCtaText = isCurrentPayg
    ? t("PRICING.PLAN_TYPES.CURRENT_PLAN")
    : isTrial
      ? t("PRICING.ACTION.CONTINUE_PAYG")
      : t("PRICING.ACTION.PAY_AS_YOU_GO");

  const proCtaText = isCurrentPro
    ? isPendingCancellation
      ? t("PRICING.ACTION.CONTACT_SALES")
      : hasProChanges
        ? t("PRICING.ACTION.UPDATE_SUBSCRIPTION")
        : t("PRICING.PLAN_TYPES.CURRENT_PLAN")
    : isCurrentCustom
      ? t("PRICING.ACTION.CONTACT_SALES")
      : isExpiredTrial
        ? t("PRICING.ACTION.START_PRO")
        : isTrial || isCurrentPayg
          ? t("PRICING.ACTION.SUBSCRIBE_NOW")
          : t("PRICING.ACTION.UPDATE_SUBSCRIPTION");

  const proIsPendingRetry = isPendingPayment && isCurrentPro;
  const customIsPendingRetry = isPendingPayment && isCurrentCustom;

  function buildRetryCtaText(): string {
    if (subscription?.paymentSource === PaymentSourceType.Stripe) {
      const total = (subscription?.cost?.total ?? 0).toLocaleString();
      return t("PRICING.RETRY.PAY_WITH_CARDS", "Pay with cards {{total}}", {
        total: `$${total}`,
      });
    }
    if (subscription?.paymentSource === PaymentSourceType.Paddle) {
      return t("PAYMENT.PENDING_PAYMENT", "Pay Pending Payment");
    }
    return "";
  }

  const retryCtaText = retryProcessing
    ? t("ACTION.PROCESSING")
    : buildRetryCtaText();

  const retryCtaDisabled = retryProcessing || !canManageBilling;

  function handleMessageUs() {
    intercomSendMessage(t("PRICING.MANAGED_PLAN_MESSAGE_PREFILL", "I'd like to make a change to my plan."));
    intercomShow();
  }

  function handleContactToResume() {
    intercomSendMessage("I'd like to resume my subscription.");
    intercomShow();
  }

  if (subscriptionLoading || offersLoading) {
    return (
      <div className="mx-auto flex max-w-5xl flex-col gap-8 px-4 py-8">
        <div className="space-y-3 text-center">
          <Skeleton className="mx-auto h-8 w-80" />
          <Skeleton className="mx-auto h-4 w-64" />
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <PricingCardSkeleton />
          <PricingCardSkeleton />
          <PricingCardSkeleton />
        </div>
      </div>
    );
  }

  if (subscriptionError) {
    return (
      <div className="mx-auto flex max-w-5xl flex-col items-center gap-4 px-4 py-16 text-center">
        <h2 className="text-xl font-semibold">{t("PRICING.ERROR.TITLE")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("PRICING.ERROR.SUBSCRIPTION_DESC")}
        </p>
        <Button onClick={() => window.location.reload()} aria-label={t("PRICING.ERROR.REFRESH")}>
          {t("PRICING.ERROR.REFRESH")}
        </Button>
      </div>
    );
  }

  if (offersError) {
    return (
      <div className="mx-auto flex max-w-5xl flex-col items-center gap-4 px-4 py-16 text-center">
        <h2 className="text-xl font-semibold">{t("PRICING.ERROR.OFFERS_TITLE")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("PRICING.ERROR.OFFERS_DESC")}
        </p>
        <div className="flex gap-3">
          <Button onClick={() => window.location.reload()} aria-label={t("ACTION.TRY_AGAIN", "Try Again")}>
            {t("ACTION.TRY_AGAIN", "Try Again")}
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(SPEAK_PRICING_URL, "_blank", "noopener,noreferrer")}
            aria-label={t("PRICING.ERROR.VIEW_PRICING")}
          >
            {t("PRICING.ERROR.VIEW_PRICING")}
          </Button>
        </div>
      </div>
    );
  }

  if (proMonthly == null || proAnnual == null) {
    return (
      <div className="mx-auto flex max-w-5xl flex-col items-center gap-4 px-4 py-16 text-center">
        <h2 className="text-xl font-semibold">{t("PRICING.ERROR.OFFERS_TITLE")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("PRICING.ERROR.OFFERS_DESC")}
        </p>
        <Button onClick={() => window.location.reload()} aria-label={t("ACTION.TRY_AGAIN", "Try Again")}>
          {t("ACTION.TRY_AGAIN", "Try Again")}
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-10 px-4 py-8">
      {isPendingCancellation && subscription?.billEndDate && (
        <div className="flex items-center justify-between rounded-md border border-warning/30 bg-warning/10 p-4 text-sm text-warning">
          <span>
            {t("PRICING.SUBSCRIPTION_CANCELLED_ON", {
              date: format(new Date(subscription.billEndDate), "MMM dd, yyyy"),
            })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleContactToResume}
            aria-label={t("PRICING.RESUME_SUBSCRIPTION")}
          >
            {t("PRICING.RESUME_SUBSCRIPTION")}
          </Button>
        </div>
      )}

      {isPendingReview && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-4 text-sm text-muted-foreground">
          {t("PRICING.PENDING_REVIEW_MESSAGE")}
        </div>
      )}

      {!offersLoading && !offersError && offers && !proOffer && (
        <div className="rounded-md border border-warning/30 bg-warning/10 p-4 text-sm text-warning">
          {t("PRICING.LOAD_FAILURE_MESSAGE")}
        </div>
      )}

      <motion.div
        className="space-y-2 text-center"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
          {heroText}
        </h1>
        <p className="text-muted-foreground">
          {t("PRICING.DESC")}
        </p>
        {user?.onboarding?.goal && goalLabel && (
          <Badge className="bg-primary/10 text-primary text-xs font-semibold">
            {goalLabel}
          </Badge>
        )}
      </motion.div>

      <TrustBar />

      {!isHighValueSubscriber && (
        <BillingToggle isAnnual={isAnnual} onChange={handleToggleBilling} />
      )}

      {isSubscribedActive && (
        <div className="rounded-xl border border-primary/20 bg-primary/5 px-5 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t("PRICING.PLAN_TYPES.CURRENT_PLAN")}
            </p>
            <p className="mt-0.5 font-semibold text-foreground">
              {isCurrentPro ? t("PRICING.PLAN_TYPES.PRO") : isCurrentPayg ? t("PRICING.PLAN_TYPES.PAY_AS_YOU_GO") : t("PRICING.PLAN_TYPES.SCALE")}
            </p>
          </div>
          {isHighValueSubscriber ? (
            <ManagedPlanNote onMessageUs={handleMessageUs} />
          ) : (subscription?.cost?.monthly ?? 0) > 0 ? (
            <p className="text-sm font-medium text-muted-foreground whitespace-nowrap">
              ${subscription!.cost.monthly}/mo
            </p>
          ) : null}
        </div>
      )}

      {subscription && subscription.features && !isTrial && (
        <PlanIncludesCard
          subscription={subscription}
          isPendingCancellation={isPendingCancellation}
          isPendingReview={isPendingReview}
          isCurrentPayg={isCurrentPayg}
        />
      )}

      <div className={cn(
        "grid grid-cols-1 gap-6",
        isHighValueSubscriber
          ? "max-w-md mx-auto"
          : isSubscribedActive
            ? "md:grid-cols-2 max-w-3xl mx-auto"
            : "md:grid-cols-3"
      )}>
        {!isHighValueSubscriber && !isSubscribedActive && (
          <motion.div
            className="h-full"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0 }}
          >
            <PricingCard
              name={t("PRICING.PLAN_TYPES.PAY_AS_YOU_GO")}
              description={t("PRICING.PLAN_TYPES.PAY_AS_YOU_GO_DESC")}
              monthlyPrice={0}
              annualPrice={0}
              isAnnual={isAnnual}
              currentPlan={isCurrentPayg}
              rates={PAYG_RATES}
              ratesTitle={t("PRICING.CARD.PAYG_RATES_TITLE")}
              features={PAYG_FEATURES}
              featuresTitle={t("PRICING.CARD.PAYG_FEATURES_TITLE")}
              ctaText={paygCtaText}
              ctaVariant="secondary"
              ctaDisabled={isCurrentPayg || isCurrentCustom}
              onCtaClick={() =>
                handleSelectPlan(buildPaygPlanType(), currentDuration)
              }
              priceSubtext="Pay only for what you use"
              ctaSubLabel={isCurrentPayg ? undefined : t("PRICING.PAYG_CTA_SUBLABEL")}
              teamConfigurator={
                <div className="space-y-2">
                  <div className="flex items-center gap-3 rounded-lg bg-muted/50 px-4 py-2.5">
                    <span className="text-sm font-medium whitespace-nowrap">{t("PRICING.ONE_USER_INCLUDED")}</span>
                  </div>
                </div>
              }
            />
          </motion.div>
        )}

        {!isHighValueSubscriber && (
          <motion.div
            className="h-full"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.05 }}
          >
            <PricingCard
              name={t("PRICING.PLAN_TYPES.PRO")}
              description={t("PRICING.PLAN_TYPES.PRO_DESC")}
              monthlyPrice={proMonthly}
              annualPrice={proAnnual}
              isAnnual={isAnnual}
              currentPlan={isCurrentPro}
              badge={proBadge}
              rates={proRates}
              ratesTitle={t("PRICING.CARD.PRO_RATES_TITLE")}
              features={proFeatures}
              featuresTitle={t("PRICING.CARD.PRO_FEATURES_TITLE")}
              ctaText={proIsPendingRetry ? retryCtaText : proCtaText}
              ctaVariant="primary"
              ctaDisabled={
                proIsPendingRetry
                  ? retryCtaDisabled
                  : isCurrentPro && !hasProChanges && !isPendingCancellation
              }
              onCtaClick={
                proIsPendingRetry
                  ? handleRetryPendingPayment
                  : isCurrentPro && isPendingCancellation
                    ? handleContactToResume
                    : isCurrentCustom
                      ? handleContactSales
                      : () => {
                        const plan = buildProPlanType();
                        if (plan) handleSelectPlan(plan, currentDuration);
                      }
              }
              seats={proSeats}
              ctaSubLabel={isCurrentPro ? undefined : t("PRICING.CANCEL_ANYTIME")}
              enterpriseDiscount={proEnterpriseDiscount}
              teamConfigurator={
                proIsPendingRetry ? undefined : (
                  <TeamConfigurator
                    seats={proSeats}
                    onSeatsChange={setProSeats}
                    isAnnual={isAnnual}
                    perUserMonthly={proMonthly}
                    perUserAnnual={proAnnual}
                  />
                )
              }
            />
          </motion.div>
        )}

        <motion.div
          className="h-full"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <PricingCard
            name={t("PRICING.PLAN_TYPES.SCALE")}
            description={t("PRICING.PLAN_TYPES.SCALE_DESC")}
            monthlyPrice={null}
            annualPrice={null}
            isAnnual={isAnnual}
            featured
            badge={t("PRICING.MOST_POPULAR")}
            currentPlan={isCurrentCustom}
            ctaDisabled={customIsPendingRetry ? retryCtaDisabled : false}
            rates={CUSTOM_RATES}
            ratesTitle={t("PRICING.CARD.SCALE_RATES_TITLE")}
            features={CUSTOM_FEATURES}
            featuresTitle={t("PRICING.CARD.SCALE_FEATURES_TITLE")}
            ctaText={
              customIsPendingRetry ? retryCtaText : isCurrentCustom ? t("PRICING.ACTION.MESSAGE_US", "Message us") : isSubscribedActive ? t("PRICING.ACTION.CONTACT_SALES") : t("PRICING.ACTION.BOOK_BUILD_CONSULT", "Book a build consult")
            }
            ctaVariant={customIsPendingRetry ? "primary" : "secondary"}
            onCtaClick={
              customIsPendingRetry ? handleRetryPendingPayment : isCurrentCustom ? handleMessageUs : handleContactSales
            }
            ctaSubLabel={
              customIsPendingRetry ? undefined : t("PRICING.RESPONDS_TIME")
            }
            teamConfigurator={
              <div className="space-y-2">
                <div className="flex items-center gap-3 rounded-lg bg-muted/50 px-4 py-2.5">
                  <span className="text-sm font-medium whitespace-nowrap">{t("PRICING.UNLIMITED_USERS")}</span>
                  <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
                    {t("PRICING.VOLUME_DISCOUNTS")}
                  </span>
                </div>
              </div>
            }
          />
        </motion.div>
      </div>

      {isCurrentPayg &&
        (subscription?.cost?.total ?? 0) > (isAnnual ? proAnnual : proMonthly) && (
          <div className="mx-auto max-w-[480px] rounded-xl border border-success/20 bg-success/5 px-4 py-2.5 text-center text-sm font-medium text-success">
            {t("PRICING.SAVINGS_NUDGE", {
              amount: `$${Math.round(
                (subscription?.cost?.total ?? 0) - (isAnnual ? proAnnual : proMonthly)
              )}`,
            })}
          </div>
        )}

      {isRevenueCat && (
        <div className="mx-auto max-w-md rounded-md border border-primary/30 bg-primary/5 p-4 text-center text-sm text-muted-foreground">
          {t("PRICING.REVENUECAT_MANAGED_NOTE")}
        </div>
      )}

      {subscription &&
        !isHighValueSubscriber &&
        subscription.planId !== SubscriptionPlanId.Speak_Free_Trial &&
        subscription.planId !== SubscriptionPlanId.Speak_Pay_As_You_Go &&
        !isRevenueCat &&
        !isPendingCancellation &&
        !isPendingReview &&
        !isPendingPayment && (
          <div className="flex justify-center">
            {isDirectDeposit ? (
              <button
                onClick={handleContactSales}
                className="py-2 text-sm text-muted-foreground underline transition-colors hover:text-foreground"
                aria-label={t("PRICING.CONTACT_TO_CANCEL")}
              >
                {t("PRICING.CONTACT_TO_CANCEL")}
              </button>
            ) : (
              <button
                onClick={() => {
                  if (!canManageBilling) {
                    toast.error(t("PAYMENT.NO_BILLING_PERMISSION", "Contact your account admin to manage billing."));
                    return;
                  }
                  setShowCancelFlow(true);
                }}
                className="py-2 text-sm text-muted-foreground underline transition-colors hover:text-foreground"
                aria-label={t("PRICING.ACTION.CANCEL_SUBSCRIPTION")}
              >
                {t("PRICING.ACTION.CANCEL_SUBSCRIPTION")}
              </button>
            )}
          </div>
        )}

      <TestimonialsSection />
      <UseCasesSection />
      <FaqSection />

      {showRetryAddSource && canManageBilling && (
        <AddSource
          open={showRetryAddSource}
          onClose={() => setShowRetryAddSource(false)}
          onSuccess={() => setShowRetryAddSource(false)}
          canManageBilling={canManageBilling}
        />
      )}

      {showRetryPaymentDialog && subscription && canManageBilling && (
        <RetryPaymentDialog
          open={showRetryPaymentDialog}
          onClose={() => setShowRetryPaymentDialog(false)}
          subscription={subscription}
          canManageBilling={canManageBilling}
        />
      )}

      {checkoutPlan && checkoutDuration && typeof document !== "undefined" && createPortal(
        <CreateSubscription
          open={!!checkoutPlan}
          onClose={() => {
            setCheckoutPlan(null);
            setCheckoutDuration(null);
          }}
          plan={checkoutPlan}
          duration={checkoutDuration}
          teamMembers={proSeats}
          onSuccess={() => {
            setCheckoutPlan(null);
            setCheckoutDuration(null);
            onCheckoutSuccess?.();
          }}
        />,
        document.body
      )}

      {/* Portaled — same reason as CreateSubscription above. */}
      {showCancelFlow && subscription && user?.companyId && typeof document !== "undefined" && createPortal(
        <CancelFlow
          open={showCancelFlow}
          onClose={() => setShowCancelFlow(false)}
          onComplete={() => setShowCancelFlow(false)}
          subscription={subscription}
          companyId={user.companyId}
        />,
        document.body
      )}
    </div>
  );
}

interface PricingPageProps {
  /** Fires after successful checkout — used by embedded contexts (paywall dialog) to dismiss and trigger onResume. */
  onCheckoutSuccess?: () => void;
}

export function PricingPage({ onCheckoutSuccess }: PricingPageProps = {}) {
  return (
    <Suspense
      fallback={
        <div className="mx-auto flex max-w-5xl flex-col gap-8 px-4 py-8">
          <div className="space-y-3 text-center">
            <Skeleton className="mx-auto h-8 w-80" />
            <Skeleton className="mx-auto h-4 w-64" />
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <PricingCardSkeleton />
            <PricingCardSkeleton />
            <PricingCardSkeleton />
          </div>
        </div>
      }
    >
      <PricingPageContent onCheckoutSuccess={onCheckoutSuccess} />
    </Suspense>
  );
}
